import "../../App.css";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import BackOnly from "../../components/BackOnly";
import ContactPopup from "../../components/ContactPopup";
import AffiliateBooking from "../../components/affiliate/AffiliateBooking";
import AffiliateExpedia from "../../components/affiliate/AffiliateExpedia";
import AffiliateTrip from "../../components/affiliate/AffiliateTrip";
import React, { useState, useEffect } from "react";

import { useNavigate } from "react-router-dom";   // ⭐ REQUIRED

// ⭐ STRICT COMMAND: Arizona ALWAYS goes back to USA map
export const backRoute = "/map/usa";

export default function Arizona() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // ⭐ Send strict back command to BackOnly
  useEffect(() => {
    navigate(".", { state: { backRoute } });
  }, [navigate]);

  return (
    <>
      <Header />

      <div className="visited-back-wrapper">
        <BackOnly />
      </div>


      <div className="page-container" style={{ padding: "12px" }}>
        <h1>Arizona</h1>

        <section className="travel-review">
          {/* LEFT COLUMN */}
          <div className="travel-text">
           <h2>🌵 Discover Arizona</h2>


           <p>
  Arizona is a land of contrasts, stretching from high desert plateaus to deep canyons and warm, sun‑filled valleys. 
  Located in the American Southwest, the state is known for its dry climate, clear skies, and extremely hot summers, 
  especially in the southern regions. Much of Arizona is desert, but the landscape changes quickly as you move north 
  toward the mountains and the Grand Canyon. The state is best known for its natural wonders, Native American history, 
  and wide‑open scenery. My visit focused on the West Rim of the Grand Canyon, located on tribal land, which offers a 
  different experience from the national park side.
</p>

 <p>
  My plan is to return and explore more of Arizona, including the South Rim of the Grand Canyon and the city of 
  Phoenix, which looks fascinating from everything I’ve seen in pictures. Arizona is also known for its extreme temperatures: 
  the highest ever recorded was 128°F (53°C) in Lake Havasu City on June 29, 1994, while the lowest was −40°F 
  (−40°C) in Hawley Lake on January 7, 1971. Phoenix itself is famous for its hot summers, outdoor events, 
  spring training baseball, desert gardens, and vibrant food scene. Arizona also borders Mexico to the south, 
  which adds a strong cultural influence to the region’s food, traditions, and history.
</p>


            

            <h3>🚗 Getting There</h3>

           <p>
  Arizona can be reached whether you are flying in or driving from a nearby state. Most visitors arrive through 
  major airports such as Phoenix Sky Harbor International Airport (PHX), Tucson International Airport (TUS), or 
  smaller regional airports depending on their destination. The state is also connected by several major highways, 
  making it accessible for road trips from Nevada, Utah, New Mexico, and California.
</p>


           <p>
  My own experience getting to Arizona was by car from Nevada. I was staying in Las Vegas and drove toward the 
  West Rim of the Grand Canyon, passing through Hoover Dam and continuing along Route 93. The drive has that 
  classic desert scenery—wide open views, long stretches of road, and a few gas stations and pullouts where you 
  can stop to admire the landscape. Once I turned onto Pierce Ferry Road, the area became more populated, with 
  more houses and people around. The entire drive from Hoover Dam to the West Rim took me about two hours.
</p>

            <h3>🌟 My Personal Observation</h3>

            <li>
  <div className="place-block">
    <strong>West Rim of the Grand Canyon:</strong> The West Rim offers a different kind of Grand Canyon 
    experience, located on the Hualapai Indian Reservation. It features wide, dramatic canyon views and the 
    famous Skywalk — a glass bridge that extends out over the edge. Walking on the Skywalk is unforgettable, 
    with the canyon floor visible 4,000 feet below your feet. They don’t allow cameras or phones on the 
    Skywalk, so I don’t have photos from the platform itself, but I do have pictures of how it looks from 
    the outside. I’ve visited twice, and based on what I’ve read, it tends to be less crowded than the 
    South Rim, though it can still get busy during peak times.

    <p style={{ marginTop: "1rem" }}>
      There are several places to visit within the West Rim area, and here are the ones I explored, each with 
      its Google Maps location:
    </p>

    <ul>
      <li>
        <strong>Hualapai Ranch (Hualapai Town):</strong> A small Old‑West style area with cabins, a restaurant, 
        and buildings that feel like a living museum.  
        <br />
        <a
          href="https://www.google.com/maps?q=35.96444143838939,-113.808679091774"
          target="_blank"
          rel="noopener noreferrer"
        >
          📍 View on Google Maps
        </a>
      </li>
      <br />
      <li>
        <strong>Skywalk:</strong> The iconic glass bridge built in 2007, extending about 70 feet over the canyon 
        edge. The platform sits roughly 4,000 feet above the canyon floor and gives a clear view straight down 
        through the glass.  
        <br />
        <a
          href="https://www.google.com/maps?q=36.01230655795787,-113.81106247991694"
          target="_blank"
          rel="noopener noreferrer"
        >
          📍 View on Google Maps
        </a>
      </li>
      <br />

      <li>
        <strong>Guano Point:</strong> A scenic viewpoint with old remnants of the historic guano mining operation 
        that once existed in the area. It offers some of the best panoramic views of the canyon.  
        <br />
        <a
          href="https://www.google.com/maps?q=36.030281920999975,-113.82738975833945"
          target="_blank"
          rel="noopener noreferrer"
        >
          📍 View on Google Maps
        </a>
      </li>
    </ul>
  </div>
</li>




            <h3>🌄 Other Hidden Gems – West Rim</h3>

<ul>
  {/* Donkey / Mule Rides */}
  <li>
    <div className="place-block">
      <strong>Hualapai Ranch Donkey Rides:</strong> Near Hualapai Ranch, visitors can take short guided 
      rides on donkeys or mules, giving the area a classic Old‑West feel. These rides are usually offered 
      around the ranch and are great for families or anyone wanting a unique way to enjoy the desert scenery.
      <br />
      <a
        href="https://www.google.com/maps?q=35.96444143838939,-113.808679091774"
        target="_blank"
        rel="noopener noreferrer"
      >
        📍 Hualapai Ranch
      </a>
    </div>
  </li>

  {/* Helicopter Flights */}
  <li>
    <div className="place-block">
      <strong>Helicopter Flights (Grand Canyon West):</strong> Scenic helicopter tours operate from the 
      Grand Canyon West Airport, offering flights over the canyon and, on some tours, landings at the 
      bottom. These tours are popular with visitors coming from Las Vegas and provide a completely 
      different perspective of the canyon.
      <br />
      <a
        href="https://www.google.com/maps?q=35.987222,-113.820556"
        target="_blank"
        rel="noopener noreferrer"
      >
        📍 Grand Canyon West Airport (Helicopter Tours)
      </a>
    </div>
  </li>

  {/* Zipline */}
  <li>
    <div className="place-block">
      <strong>Grand Canyon West Zipline:</strong> Opened in 2018, this zipline features two runs that 
      glide over a desert canyon near the West Rim. It’s an adventurous option for visitors looking for 
      something beyond the Skywalk, reaching speeds of up to 40–50 mph.
      <br />
      <a
        href="https://www.google.com/maps?q=35.964286489774814, -113.80912489434165"
        target="_blank"
        rel="noopener noreferrer"
      >
        📍 Grand Canyon West Zipline
      </a>
    </div>
  </li>

  {/* Eagle Point */}
  <li>
    <div className="place-block">
      <strong>Eagle Point Village:</strong> A cultural area featuring traditional Native American 
      dwellings, performances, and viewpoints. This is also where the Skywalk is located, offering 
      dramatic views of the canyon walls.
      <br />
      <a
        href="https://www.google.com/maps?q=36.0119,-113.8113"
        target="_blank"
        rel="noopener noreferrer"
      >
        📍 Eagle Point
      </a>
    </div>
  </li>

  {/* Boat Ride Combo */}
  <li>
    <div className="place-block">
      <strong>Colorado River Boat Ride (Helicopter + Boat Combo):</strong> Some tours include a 
      helicopter descent to the canyon floor followed by a short boat ride along the Colorado River. 
      This experience is unique to the West Rim and offers a rare view from the bottom of the canyon.
      <br />
      <a
        href="https://www.google.com/maps?q=35.987222,-113.820556"
        target="_blank"
        rel="noopener noreferrer"
      >
        📍 Helicopter Landing Area (Tour Dependent)
      </a>
    </div>
  </li>
  
</ul>
          

 <h3>🛏️ Places to Stay – Arizona (Near the West Rim)</h3>

<p>
  The West Rim of the Grand Canyon sits on the <strong>Hualapai Reservation</strong>, far from major
  cities. Because of this, lodging options are limited but peaceful, with cabins and small inns
  offering quiet desert views and easy access to the canyon.
</p>

<p>
  These are the closest and most convenient places to stay near the West Rim. Some are rustic,
  some are modern, and several can be booked directly through Expedia.
</p>

<ul>
  <li>
    <div className="place-block">
      <strong>Cabins at Grand Canyon West:</strong> The closest lodging to the Skywalk — simple,
      clean cabins located right on Hualapai land. Perfect for sunrise and sunset views.
    </div>
  </li>

  <li>
    <div className="place-block">
      <strong>Grand Canyon Western Ranch:</strong> A historic ranch offering cozy cabins, horseback
      rides, campfires, and a true Old West atmosphere. About 20 minutes from the West Rim.
    </div>
  </li>

  <li>
    <div className="place-block">
      <strong>Hualapai Lodge (Peach Springs):</strong> A comfortable lodge with a restaurant and
      modern rooms. About 1 hour from the West Rim — the best option if you want more amenities.
    </div>
  </li>

  <li>
    <div className="place-block">
      <strong>Grand Canyon Glamping Resort:</strong> Luxury tents and desert views. A unique stay
      for travelers who want something different and scenic.
    </div>
  </li>

  <li>
    <div className="place-block">
      <strong>Kingman, Arizona Hotels:</strong> A great base if you prefer city amenities. Kingman
      is about 1 hour 20 minutes away and has many hotel options on Expedia.
    </div>
  </li>
</ul>


<h3>If you are looking for a place to stay, you can check prices, date and time here.</h3>
            <div className="affiliate-block">
              <AffiliateExpedia />
              <AffiliateBooking />
              <AffiliateTrip />
            </div>

<h3>🎥 My Travel Videos</h3>

<p>Here is a video from Skywalk and Guano Point. Hope you enjoy them!</p>

<p>
  <a
    href="https://youtu.be/I49ljVgPZKA?si=RzH4ZLXuzQbd9mwP"
    target="_blank"
    rel="noopener noreferrer"
  >
    West Rim 
  </a>
  <br />
  
</p>
</div> {/* LEFT COLUMN closes */}

{/* RIGHT COLUMN */}
<div className="travel-image-column">
  <a
    href="https://youtu.be/I49ljVgPZKA?si=jUADt0jocgj6ZG9x"
    target="_blank"
    rel="noopener noreferrer"
    className="youtube-image-card"
  >
    <img
      src="/images/usa/arizona/sw1.jpg"
      alt="Skywalk Video"
    />
  </a>
   <img src="/images/usa/arizona/sw2.jpg" alt="Guano Point" />
  <img src="/images/usa/arizona/gp1.jpg" alt="Guano Point" />
  <img src="/images/usa/arizona/gp2.jpg" alt="Guano Point" />
  <img src="/images/usa/arizona/gp4.jpg" alt="Guano Point" />
  <img src="/images/usa/arizona/gp5.jpg" alt="Guano Point" />
  <img src="/images/usa/arizona/gp6.jpg" alt="Guano Point" />
  <img src="/images/usa/arizona/gp7.jpg" alt="Guano Point" />
  <img src="/images/usa/arizona/gp8.jpg" alt="Guano Point" />
  <img src="/images/usa/arizona/gp9.jpg" alt="Guano Point" />
  <img src="/images/usa/arizona/h1.jpg" alt="Reservoir" />
  <img src="/images/usa/arizona/h2.jpg" alt="Reservoir" />
  <img src="/images/usa/arizona/casetas.jpg" alt="Tents" />
  <img src="/images/usa/arizona/house1.jpg" alt="Tent" />
  <img src="/images/usa/arizona/house2.jpg" alt="Tent" />
  <img src="/images/usa/arizona/house4.jpg" alt="Tent" />
  <img src="/images/usa/arizona/eagle.jpg" alt="Eagle Point" />
</div> {/* RIGHT COLUMN closes */}

</section>
</div>

{/* EMAIL BUTTON */}
<div className="email-wrapper">
  <button className="email-link" onClick={() => setOpen(true)}>
    contact@thefindingspot.com
  </button>
</div>

{/* POPUP */}
<ContactPopup isOpen={open} onClose={() => setOpen(false)} />

<Footer />

</>
);
}
